import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import api, { mediaUrl, uploadImage, errText } from '../lib/api';
import RichTextEditor from './RichTextEditor';
import { COUNTRIES } from '../lib/countries';
import { useToast } from '../hooks/use-toast';

const BLANK = {
  type: 'event', title: '', short_description: '', description: '',
  meta_title: '', meta_description: '', meta_keywords: [], og_image_url: '',
  thumbnail_url: '', featured_image_url: '', gallery_urls: [],
  start_datetime: '', end_datetime: '', is_multiday: false,
  time_of_day: '', duration_minutes: '', timezone: 'America/Chicago',
  recurrence: { frequency: 'none', days_of_week: [], days_of_month: [], until: null },
  registration_deadline: '', itinerary: [], faqs: [],
  inclusions: [], what_to_bring: [],
  category: '', tags: [], level: 'all', languages: [],
  mode: 'in_person', online_url: '', joining_instructions: '', location: '', country: '', center_id: '', capacity: '',
  capacity_online: '', capacity_in_person: '', price_online: '', price_in_person: '', per_day_price_online: '', per_day_price_in_person: '',
  facilitator_names: [], facilitator_ids: [], is_free: true, pricing_type: 'free', price: 0, accept_donations: false,
  per_day_price: 0, allow_day_selection: false, allow_multiple_registrants: true,
  addons: [], currency: 'usd', inclusions: [], exclusions: [], what_to_bring: [],
  waiver_required: false, waiver_text: '',
};

const centerAddress = (c) => {
  const l = c.location || {};
  return [c.name, l.address, l.city, l.state, l.country, l.pincode].filter(Boolean).join(', ');
};

const WEEKDAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : '');

const TIMEZONES = [
  ['America/Chicago', 'US Central (Chicago)'], ['America/New_York', 'US Eastern (New York)'],
  ['America/Denver', 'US Mountain (Denver)'], ['America/Los_Angeles', 'US Pacific (Los Angeles)'],
  ['Asia/Kolkata', 'India (Kolkata)'], ['Europe/London', 'UK (London)'],
  ['Europe/Berlin', 'Central Europe (Berlin)'], ['Asia/Dubai', 'Gulf (Dubai)'],
  ['Asia/Singapore', 'Singapore'], ['Australia/Sydney', 'Australia (Sydney)'], ['UTC', 'UTC'],
];

const ProgramForm = ({ program, onDone, onCancel }) => {
  const { toast } = useToast();
  const init = program ? {
    ...BLANK, ...program,
    start_datetime: toLocalInput(program.start_datetime),
    end_datetime: toLocalInput(program.end_datetime),
    registration_deadline: toLocalInput(program.registration_deadline),
    recurrence: program.recurrence || BLANK.recurrence,
    capacity: program.capacity ?? '',
    capacity_online: program.capacity_online ?? '',
    capacity_in_person: program.capacity_in_person ?? '',
    price_online: program.price_online ?? '',
    price_in_person: program.price_in_person ?? '',
    per_day_price_online: program.per_day_price_online ?? '',
    per_day_price_in_person: program.per_day_price_in_person ?? '',
  } : BLANK;
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/centers').then((r) => setCenters(r.data || [])).catch((e) => console.error('Failed to load centers:', e));
    api.get('/teachers').then((r) => setTeachers(r.data || [])).catch((e) => console.error('Failed to load teachers:', e));
  }, []);

  const upload = async (file, cb) => {
    try { const url = await uploadImage(file); cb(url); toast({ title: 'Image uploaded' }); }
    catch (e) { console.error('Upload failed:', e); toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); }
  };

  const uploadGallery = async (files) => {
    setGalleryUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const url = await uploadImage(file);
        urls.push(url);
      }
      setF((p) => ({ ...p, gallery_urls: [...(p.gallery_urls || []), ...urls] }));
      toast({ title: `${urls.length} image${urls.length > 1 ? 's' : ''} added to gallery` });
    } catch { toast({ title: 'Some images failed to upload' }); }
    finally { setGalleryUploading(false); }
  };

  const save = async () => {
    if (!f.title) return toast({ title: 'Title is required' });
    setSaving(true);
    const hybrid = f.mode === 'hybrid';
    const numOrNull = (v) => (v !== '' && v != null ? parseFloat(v) : null);
    const pInPerson = numOrNull(f.price_in_person);
    const pOnline = numOrNull(f.price_online);
    const ppInPerson = numOrNull(f.per_day_price_in_person);
    const ppOnline = numOrNull(f.per_day_price_online);
    const payload = {
      ...f,
      is_free: f.pricing_type === 'free',
      price: hybrid ? (pInPerson ?? pOnline ?? parseFloat(f.price) ?? 0) : (parseFloat(f.price) || 0),
      per_day_price: hybrid ? (ppInPerson ?? ppOnline ?? parseFloat(f.per_day_price) ?? 0) : (parseFloat(f.per_day_price) || 0),
      price_online: hybrid ? pOnline : null,
      price_in_person: hybrid ? pInPerson : null,
      per_day_price_online: hybrid ? ppOnline : null,
      per_day_price_in_person: hybrid ? ppInPerson : null,
      capacity: f.capacity ? parseInt(f.capacity, 10) : null,
      capacity_online: hybrid && f.capacity_online ? parseInt(f.capacity_online, 10) : null,
      capacity_in_person: hybrid && f.capacity_in_person ? parseInt(f.capacity_in_person, 10) : null,
      duration_minutes: f.duration_minutes ? parseInt(f.duration_minutes, 10) : null,
      start_datetime: f.start_datetime || null,
      end_datetime: f.end_datetime || null,
      registration_deadline: f.registration_deadline || null,
      suggested_donation: parseFloat(f.suggested_donation) || 0,
      donation_min: numOrNull(f.donation_min),
      donation_allow_zero: !!f.donation_allow_zero && !numOrNull(f.donation_min),
      donation_value_received: parseFloat(f.donation_value_received) || 0,
    };
    // drop server-managed fields
    ['id', 'slug', 'status', 'is_featured', 'feature_order', 'created_by', 'created_by_role', 'created_at', 'updated_at', 'rejection_reason'].forEach((k) => delete payload[k]);
    try {
      if (program) await api.patch(`/programs/${program.id}`, payload);
      else await api.post('/programs', payload);
      toast({ title: program ? 'Program updated' : 'Program created' });
      onDone();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setSaving(false); }
  };

  const addRow = (key, row) => set(key, [...(f[key] || []), row]);
  const updRow = (key, i, patch) => set(key, f[key].map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const delRow = (key, i) => set(key, f[key].filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <Sel label="Type" value={f.type} onChange={(e) => set('type', e.target.value)} options={[['event', 'Event'], ['retreat', 'Retreat'], ['class', 'Class']]} />
        <Txt label="Title*" value={f.title} onChange={(e) => set('title', e.target.value)} />
      </div>
      <Txt label="Short description" value={f.short_description} onChange={(e) => set('short_description', e.target.value)} />
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Full description</span>
        <RichTextEditor value={f.description} onChange={(html) => set('description', html)} />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <ImageField label="Thumbnail" url={f.thumbnail_url} onUpload={(file) => upload(file, (u) => set('thumbnail_url', u))} onClear={() => set('thumbnail_url', '')} />
        <ImageField label="Featured image" url={f.featured_image_url} onUpload={(file) => upload(file, (u) => set('featured_image_url', u))} onClear={() => set('featured_image_url', '')} />
      </div>
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Gallery images</span>
        <div className="flex flex-wrap items-center gap-2">
          {(f.gallery_urls || []).map((g, i) => (
            <div key={g} className="relative" data-testid={`gallery-item-${i}`}>
              <img src={mediaUrl(g)} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <button type="button" onClick={() => set('gallery_urls', f.gallery_urls.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none" data-testid={`gallery-remove-${i}`}>×</button>
            </div>
          ))}
          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-rose/50 flex items-center justify-center cursor-pointer bg-rose-soft/30 hover:bg-rose-soft/50 transition-colors" data-testid="gallery-upload-label">
            {galleryUploading ? <Loader2 className="w-5 h-5 animate-spin text-mahogany" /> : <Plus className="w-5 h-5 text-mahogany/70" />}
            <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="gallery-upload-input"
              onChange={(e) => { if (e.target.files.length) { uploadGallery(e.target.files); e.target.value = ''; } }} />
          </label>
        </div>
        <p className="text-xs text-mahogany/60 mt-1.5">Select multiple images at once to add them to the gallery.</p>
      </div>

      {f.type !== 'class' ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <Txt label="Start date & time" type="datetime-local" value={f.start_datetime} onChange={(e) => set('start_datetime', e.target.value)} />
          <Txt label="End date & time" type="datetime-local" value={f.end_datetime} onChange={(e) => set('end_datetime', e.target.value)} />
          <Sel label="Timezone" value={f.timezone} onChange={(e) => set('timezone', e.target.value)} options={TIMEZONES} data-testid="program-timezone" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          <Txt label="Time of day" type="time" value={f.time_of_day} onChange={(e) => set('time_of_day', e.target.value)} />
          <Txt label="Duration (minutes)" type="number" value={f.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} />
          <Sel label="Timezone" value={f.timezone} onChange={(e) => set('timezone', e.target.value)} options={TIMEZONES} data-testid="program-timezone" />
        </div>
      )}
      <p className="text-[11px] text-mahogany/60 -mt-1">Enter the event's local start/end time and pick its timezone. Times are shown to attendees exactly as entered.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Sel label="Recurrence" value={f.recurrence?.frequency || 'none'} onChange={(e) => set('recurrence', { ...f.recurrence, frequency: e.target.value })} options={[['none', 'One-time'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]} />
        <Sel label="Mode" value={f.mode} onChange={(e) => set('mode', e.target.value)} options={[['in_person', 'In person'], ['online', 'Online'], ['hybrid', 'Hybrid']]} />
      </div>

      {f.recurrence?.frequency && f.recurrence.frequency !== 'none' && (
        <div className="p-4 rounded-2xl bg-rose-soft/20 border border-rose/40 space-y-3" data-testid="recurrence-options">
          {f.recurrence.frequency === 'weekly' && (
            <div>
              <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-2">Repeats on (days of week)</span>
              <div className="flex flex-wrap gap-2" data-testid="recurrence-days-of-week">
                {WEEKDAYS.map(([k, l]) => {
                  const on = (f.recurrence.days_of_week || []).includes(k);
                  return (
                    <button type="button" key={k} data-testid={`dow-${k}`}
                      onClick={() => set('recurrence', { ...f.recurrence, days_of_week: on ? f.recurrence.days_of_week.filter((d) => d !== k) : [...(f.recurrence.days_of_week || []), k] })}
                      className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${on ? 'bg-mahogany text-cream' : 'bg-white text-mahogany border border-rose/50 hover:bg-rose-soft/40'}`}>{l}</button>
                  );
                })}
              </div>
            </div>
          )}
          {f.recurrence.frequency === 'monthly' && (
            <div>
              <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-2">Repeats on (dates of month)</span>
              <div className="flex flex-wrap gap-1.5" data-testid="recurrence-days-of-month">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => {
                  const on = (f.recurrence.days_of_month || []).includes(n);
                  return (
                    <button type="button" key={n} data-testid={`dom-${n}`}
                      onClick={() => set('recurrence', { ...f.recurrence, days_of_month: on ? f.recurrence.days_of_month.filter((d) => d !== n) : [...(f.recurrence.days_of_month || []), n].sort((a, b) => a - b) })}
                      className={`w-9 h-9 rounded-lg text-sm transition-colors ${on ? 'bg-mahogany text-cream' : 'bg-white text-mahogany border border-rose/50 hover:bg-rose-soft/40'}`}>{n}</button>
                  );
                })}
              </div>
              <span className="text-[11px] text-mahogany/60 mt-1.5 block">Pick one or more dates (e.g. 1 &amp; 15 for twice a month).</span>
            </div>
          )}
          {f.recurrence.frequency === 'daily' && (
            <p className="text-sm text-mahogany/70">Repeats every day{f.recurrence.until ? ' until the date below.' : '.'}</p>
          )}
          <label className="block max-w-xs">
            <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Repeat until (optional)</span>
            <input type="date" data-testid="recurrence-until"
              value={f.recurrence.until ? String(f.recurrence.until).slice(0, 10) : ''}
              onChange={(e) => set('recurrence', { ...f.recurrence, until: e.target.value || null })}
              className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
          </label>
        </div>
      )}
      {f.mode !== 'online' && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Use a registered center</span>
            <select value={f.center_id || ''} data-testid="program-center-select"
              onChange={(e) => {
                const c = centers.find((x) => x.id === e.target.value);
                if (c) setF((p) => ({ ...p, center_id: c.id, location: centerAddress(c) }));
                else set('center_id', '');
              }}
              className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">
              <option value="">— None / custom address —</option>
              {centers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.location?.city ? ` · ${c.location.city}` : ''}</option>)}
            </select>
            <span className="text-[11px] text-mahogany/60">Pick an approved ashram/center to auto-fill the address.</span>
          </label>
          <Txt label="Location / Venue" value={f.location} onChange={(e) => set('location', e.target.value)} />
          <Sel label="Country" value={f.country || ''} onChange={(e) => set('country', e.target.value)}
            options={[['', '— Select country —'], ...COUNTRIES.map((c) => [c, c])]} data-testid="program-country-select" />
        </div>
      )}
      {f.mode !== 'in_person' && <Txt label="Online meeting link" value={f.online_url} onChange={(e) => set('online_url', e.target.value)} />}
      {f.mode !== 'in_person' && (
        <label className="block">
          <span className="text-sm text-mahogany">Joining instructions <span className="text-mahogany/50">(Meeting ID, passcode, dial-in — shared with attendees on registration)</span></span>
          <textarea value={f.joining_instructions} onChange={(e) => set('joining_instructions', e.target.value)} rows={3} data-testid="program-joining-instructions"
            placeholder={'e.g.\nMeeting ID: 123 4567 8901\nPasscode: osho108\nOr join by phone: +1 555-000-0000'}
            className="mt-1 w-full rounded-xl border border-rose/50 bg-white/70 px-4 py-2.5 text-burgundy focus:outline-none whitespace-pre-wrap" />
        </label>
      )}

      <div className="grid sm:grid-cols-4 gap-3">
        <Sel label="Level" value={f.level} onChange={(e) => set('level', e.target.value)} options={[['all', 'All levels'], ['beginner', 'Beginner'], ['intermediate', 'Intermediate'], ['advanced', 'Advanced']]} />
        <Txt label="Category" value={f.category} onChange={(e) => set('category', e.target.value)} />
        {f.mode !== 'hybrid' && <Txt label="Capacity" type="number" value={f.capacity} onChange={(e) => set('capacity', e.target.value)} />}
        <Txt label="Registration deadline" type="datetime-local" value={f.registration_deadline} onChange={(e) => set('registration_deadline', e.target.value)} />
      </div>
      {f.mode === 'hybrid' && (
        <div className="grid sm:grid-cols-2 gap-3 p-4 rounded-2xl bg-rose-soft/20 border border-rose/40" data-testid="hybrid-capacity-block">
          <Txt label="In-person capacity" type="number" value={f.capacity_in_person} onChange={(e) => set('capacity_in_person', e.target.value)} data-testid="capacity-in-person" />
          <Txt label="Online capacity" type="number" value={f.capacity_online} onChange={(e) => set('capacity_online', e.target.value)} data-testid="capacity-online" />
          <p className="sm:col-span-2 text-[11px] text-mahogany/60 -mt-1">Leave a field blank for unlimited seats in that mode. Attendees choose how they attend at checkout.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <CommaField label="Tags (comma separated)" value={f.tags} onChange={(v) => set('tags', v)} testid="program-tags" />
        <CommaField label="Languages (comma separated)" value={f.languages} onChange={(v) => set('languages', v)} testid="program-languages" />
      </div>
      <FacilitatorPicker teachers={teachers} names={f.facilitator_names || []} ids={f.facilitator_ids || []}
        onChange={(names, ids) => setF((p) => ({ ...p, facilitator_names: names, facilitator_ids: ids }))} />

      <div className="p-5 rounded-2xl bg-rose-soft/30 border border-rose/40 space-y-4">
        <div className="font-serif-display text-xl text-burgundy">Pricing</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Sel label="Pricing type" value={f.pricing_type} onChange={(e) => set('pricing_type', e.target.value)}
            options={[['free', 'Free'], ['flat', 'Flat (full event)'], ['per_day', 'Per day'], ['flat_or_per_day', 'Full retreat OR per day'], ['tickets', 'Ticketed (multiple types)'], ['enquiry', 'Tickets & registration by phone only'], ['donation', 'Suggested donation']]} />
          {f.pricing_type === 'free' && (
            <label className="col-span-2 flex items-center gap-3 cursor-pointer mt-1" data-testid="accept-donations-toggle">
              <input type="checkbox" checked={!!f.accept_donations} onChange={(e) => set('accept_donations', e.target.checked)} className="w-4 h-4 accent-burgundy" />
              <span className="text-sm text-burgundy">Invite an optional donation at registration (community-supported)</span>
            </label>
          )}
          {(f.pricing_type === 'flat' || f.pricing_type === 'flat_or_per_day') && (
            f.mode === 'hybrid' ? (
              <>
                <Txt label="In-person price (full event)" type="number" value={f.price_in_person} onChange={(e) => set('price_in_person', e.target.value)} data-testid="price-in-person" />
                <Txt label="Online price (full event)" type="number" value={f.price_online} onChange={(e) => set('price_online', e.target.value)} data-testid="price-online" />
              </>
            ) : (
              <Txt label="Full event price" type="number" value={f.price} onChange={(e) => set('price', e.target.value)} />
            )
          )}
          {(f.pricing_type === 'per_day' || f.pricing_type === 'flat_or_per_day') && (
            f.mode === 'hybrid' ? (
              <>
                <Txt label="In-person price / day" type="number" value={f.per_day_price_in_person} onChange={(e) => set('per_day_price_in_person', e.target.value)} data-testid="per-day-in-person" />
                <Txt label="Online price / day" type="number" value={f.per_day_price_online} onChange={(e) => set('per_day_price_online', e.target.value)} data-testid="per-day-online" />
              </>
            ) : (
              <Txt label="Price per day" type="number" value={f.per_day_price} onChange={(e) => set('per_day_price', e.target.value)} />
            )
          )}
          {f.pricing_type !== 'free' && <Sel label="Currency" value={f.currency} onChange={(e) => set('currency', e.target.value)} options={[['usd', 'USD'], ['inr', 'INR']]} />}
        </div>
        {f.pricing_type === 'flat_or_per_day' && (
          <p className="text-xs text-mahogany/70 -mt-1">Attendees choose to pay for the full retreat, or pay per day they attend.</p>
        )}

        {f.pricing_type === 'donation' && (
          <div className="rounded-2xl border border-rose/40 bg-rose-soft/20 p-4 space-y-3" data-testid="donation-config">
            <div className="grid sm:grid-cols-3 gap-3">
              <Txt label="Suggested donation (per person)" type="number" value={f.suggested_donation ?? ''} onChange={(e) => set('suggested_donation', e.target.value)} data-testid="suggested-donation" />
              <Txt label="Minimum gift (optional)" type="number" value={f.donation_min ?? ''} onChange={(e) => set('donation_min', e.target.value)} data-testid="donation-min" placeholder="No minimum" />
              <Txt label="Est. value received (per person)" type="number" value={f.donation_value_received ?? ''} onChange={(e) => set('donation_value_received', e.target.value)} data-testid="donation-value" placeholder="e.g. lodging + meals" />
            </div>
            <label className="flex items-center gap-2 text-sm text-burgundy"><input type="checkbox" checked={!!f.donation_allow_zero} disabled={!!parseFloat(f.donation_min)} onChange={(e) => set('donation_allow_zero', e.target.checked)} data-testid="donation-allow-zero" /> Allow registering without a gift ("I can't give right now")</label>
            <p className="text-xs text-mahogany/70">Attendees choose suggested, minimum, more, or their own amount. Receipts show EIN 92-1474053; the estimated value received (fair value of lodging/meals/program) is printed so donors know the tax-deductible portion. Leave it 0 for online classes with nothing received in return.</p>
          </div>
        )}

        {f.pricing_type === 'enquiry' && (
          <div className="rounded-2xl border border-rose/40 bg-rose-soft/20 p-4 space-y-2" data-testid="enquiry-config">
            <Txt label="Enquiry email (where registration requests are sent)" type="email" value={f.enquiry_email || ''} onChange={(e) => set('enquiry_email', e.target.value)} data-testid="enquiry-email" placeholder="e.g. registrations@yourcenter.org" />
            <p className="text-xs text-mahogany/70">Attendees won't pay online — the "Book" button becomes a "Request to Register" form. Each request is emailed here (falls back to your account email if left blank) and logged in the CRM.</p>
          </div>
        )}

        {f.pricing_type === 'tickets' && (
          <ListEditor title="Ticket types (name, price & quantity available)" rows={f.ticket_types}
            onAdd={() => addRow('ticket_types', { name: '', price: 0, quantity_total: '' })}
            onDel={(i) => delRow('ticket_types', i)}
            render={(row, i) => (
              <div className="grid sm:grid-cols-3 gap-2" data-testid={`ticket-row-${i}`}>
                <input placeholder="Name (e.g. Early Bird)" value={row.name || ''} onChange={(e) => updRow('ticket_types', i, { name: e.target.value })} className="field" data-testid={`ticket-name-${i}`} />
                <input placeholder="Price" type="number" value={row.price ?? ''} onChange={(e) => updRow('ticket_types', i, { price: parseFloat(e.target.value) || 0 })} className="field" data-testid={`ticket-price-${i}`} />
                <input placeholder="Total qty (blank = unlimited)" type="number" value={row.quantity_total ?? ''} onChange={(e) => updRow('ticket_types', i, { quantity_total: e.target.value ? parseInt(e.target.value, 10) : null })} className="field" data-testid={`ticket-qty-${i}`} />
              </div>
            )} />
        )}
        {f.pricing_type === 'tickets' && (f.ticket_types || []).length === 0 && (
          <p className="text-xs text-mahogany/70 -mt-1">Add at least one ticket type. Leave quantity blank for unlimited.</p>
        )}

        {f.pricing_type !== 'free' && f.pricing_type !== 'tickets' && (
          <div className="flex flex-wrap gap-6">
            {(f.type !== 'class') && (f.pricing_type === 'per_day' || f.pricing_type === 'flat_or_per_day') && (
              <label className="flex items-center gap-2 text-sm text-burgundy">
                <input type="checkbox" checked={f.allow_day_selection} onChange={(e) => set('allow_day_selection', e.target.checked)} />
                Let attendees choose number of days
              </label>
            )}
            <label className="flex items-center gap-2 text-sm text-burgundy">
              <input type="checkbox" checked={f.allow_multiple_registrants} onChange={(e) => set('allow_multiple_registrants', e.target.checked)} />
              Allow multiple registrants (quantity)
            </label>
          </div>
        )}

        {/* Waiver */}
        <div className="pt-4 border-t border-rose/40 space-y-2">
          <label className="flex items-center gap-2 text-sm text-burgundy">
            <input type="checkbox" checked={!!f.waiver_required} onChange={(e) => set('waiver_required', e.target.checked)} data-testid="program-waiver-required" />
            Require a signed waiver before a guest can be checked in
          </label>
          <div>
            <div className="text-xs uppercase tracking-widest text-mahogany/60 mb-1">Waiver text override (optional)</div>
            <textarea value={f.waiver_text || ''} onChange={(e) => set('waiver_text', e.target.value)} rows={4}
              placeholder="Leave blank to use the global default waiver. Enter text here only to override it for this program."
              data-testid="program-waiver-text"
              className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
          </div>
        </div>


        <ListEditor title="Add-ons (e.g. Shared Accommodation)" rows={f.addons}
          onAdd={() => addRow('addons', { name: '', price: 0, unit_label: '', max_quantity: '' })}          onDel={(i) => delRow('addons', i)}
          render={(row, i) => (
            <div className="grid sm:grid-cols-4 gap-2">
              <input placeholder="Name (e.g. Shared Room)" value={row.name || ''} onChange={(e) => updRow('addons', i, { name: e.target.value })} className="field" />
              <input placeholder="Price" type="number" value={row.price ?? ''} onChange={(e) => updRow('addons', i, { price: parseFloat(e.target.value) || 0 })} className="field" />
              <input placeholder="Unit (e.g. per night)" value={row.unit_label || ''} onChange={(e) => updRow('addons', i, { unit_label: e.target.value })} className="field" />
              <input placeholder="Max qty (optional)" type="number" value={row.max_quantity ?? ''} onChange={(e) => updRow('addons', i, { max_quantity: e.target.value ? parseInt(e.target.value, 10) : null })} className="field" />
            </div>
          )} />
      </div>

      <div className="p-5 rounded-2xl bg-cream-2/60 border border-rose/40 space-y-4">
        <div>
          <div className="font-serif-display text-xl text-burgundy">SEO &amp; Sharing</div>
          <p className="text-xs text-mahogany/70">Control how this program appears in Google results and social shares. Leave blank to use the title &amp; short description.</p>
        </div>
        <Txt label="Meta title" value={f.meta_title} onChange={(e) => set('meta_title', e.target.value)} placeholder="e.g. 7-Day Silence Retreat | Osho Universal" />
        <Area label="Meta description" value={f.meta_description} onChange={(e) => set('meta_description', e.target.value)} rows={2} placeholder="A concise 150–160 character summary for search engines." />
        <CommaField label="Keywords (comma separated)" value={f.meta_keywords} onChange={(v) => set('meta_keywords', v)} placeholder="meditation, retreat, silence" testid="program-keywords" />
        <ImageField label="Social share image (og:image)" url={f.og_image_url} onUpload={(file) => upload(file, (u) => set('og_image_url', u))} onClear={() => set('og_image_url', '')} />
      </div>

      <ListEditor title="Itinerary (hour-by-hour)" rows={f.itinerary} onAdd={() => addRow('itinerary', { label: '', time: '', title: '', description: '' })} onDel={(i) => delRow('itinerary', i)}
        render={(row, i) => (
          <div className="grid sm:grid-cols-4 gap-2">
            <input placeholder="Day/Label" value={row.label || ''} onChange={(e) => updRow('itinerary', i, { label: e.target.value })} className="field" />
            <input placeholder="Time" value={row.time || ''} onChange={(e) => updRow('itinerary', i, { time: e.target.value })} className="field" />
            <input placeholder="Title" value={row.title || ''} onChange={(e) => updRow('itinerary', i, { title: e.target.value })} className="field" />
            <input placeholder="Description" value={row.description || ''} onChange={(e) => updRow('itinerary', i, { description: e.target.value })} className="field" />
          </div>
        )} />

      <ListEditor title="FAQs" rows={f.faqs} onAdd={() => addRow('faqs', { question: '', answer: '' })} onDel={(i) => delRow('faqs', i)}
        render={(row, i) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Question" value={row.question || ''} onChange={(e) => updRow('faqs', i, { question: e.target.value })} className="field" />
            <input placeholder="Answer" value={row.answer || ''} onChange={(e) => updRow('faqs', i, { answer: e.target.value })} className="field" />
          </div>
        )} />

      <div className="grid sm:grid-cols-2 gap-3">
        <LinesField label="Inclusions (one per line)" value={f.inclusions} onChange={(v) => set('inclusions', v)} rows={4} placeholder={"Accommodation\nMeals\nMeditation sessions"} testid="program-inclusions" />
        <LinesField label="Exclusions (one per line)" value={f.exclusions} onChange={(v) => set('exclusions', v)} rows={4} placeholder={"Travel to venue\nPersonal expenses"} testid="program-exclusions" />
      </div>
      <LinesField label="What to bring (one per line)" value={f.what_to_bring} onChange={(v) => set('what_to_bring', v)} rows={3} placeholder={"Comfortable clothes\nWater bottle"} testid="program-whattobring" />

      <div className="flex gap-3 pt-2">
        <button onClick={save} disabled={saving} className="pill-btn justify-center flex-1">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {program ? 'Save Changes' : 'Create Program'}</button>
        <button onClick={onCancel} className="pill-btn-outline">Cancel</button>
      </div>
    </div>
  );
};

const Txt = ({ label, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <input {...p} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
);
const Area = ({ label, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <textarea {...p} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
);
const Sel = ({ label, options, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <select {...p} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
);
const ImageField = ({ label, url, onUpload, onClear }) => (
  <div><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <div className="flex items-center gap-2">
      {url ? <img src={mediaUrl(url)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-rose-soft/60 flex items-center justify-center"><Upload className="w-4 h-4 text-mahogany/60" /></div>}
      <label className="text-xs pill-btn-outline cursor-pointer py-1.5">Upload<input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} /></label>
      {url && <button onClick={onClear} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
    </div>
  </div>
);
const ListEditor = ({ title, rows, onAdd, onDel, render }) => (
  <div>
    <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-burgundy">{title}</span>
      <button onClick={onAdd} className="text-xs pill-btn py-1.5"><Plus className="w-3.5 h-3.5" /> Add</button></div>
    <div className="space-y-2">{(rows || []).map((row, i) => (
      <div key={row.id || i} className="flex gap-2 items-start"><div className="flex-1">{render(row, i)}</div>
        <button onClick={() => onDel(i)} className="text-red-500 mt-2"><Trash2 className="w-4 h-4" /></button></div>
    ))}</div>
  </div>
);

// Keeps a raw text buffer so users can freely type commas/spaces; parent always gets the parsed array.
const CommaField = ({ label, value, onChange, placeholder, testid }) => {
  const [text, setText] = useState(() => (value || []).join(', '));
  const handle = (e) => { const t = e.target.value; setText(t); onChange(t.split(',').map((s) => s.trim()).filter(Boolean)); };
  return (
    <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
      <input value={text} onChange={handle} placeholder={placeholder} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
  );
};

// Keeps a raw text buffer so Enter (new line) and spaces work naturally; parent gets one item per line.
const LinesField = ({ label, value, onChange, rows = 3, placeholder, testid }) => {
  const [text, setText] = useState(() => (value || []).join('\n'));
  const handle = (e) => { const t = e.target.value; setText(t); onChange(t.split('\n').map((s) => s.trim()).filter(Boolean)); };
  return (
    <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
      <textarea value={text} onChange={handle} rows={rows} placeholder={placeholder} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
  );
};

const FacilitatorPicker = ({ teachers, names, onChange }) => {
  const [custom, setCustom] = useState('');
  const commit = (newNames) => {
    const ids = teachers.filter((t) => newNames.includes(t.name)).map((t) => t.id);
    onChange(newNames, ids);
  };
  const addTeacher = (id) => { const t = teachers.find((x) => x.id === id); if (t && !names.includes(t.name)) commit([...names, t.name]); };
  const addCustom = () => { const v = custom.trim(); if (v && !names.includes(v)) { commit([...names, v]); setCustom(''); } };
  const remove = (n) => commit(names.filter((x) => x !== n));
  const available = teachers.filter((t) => !names.includes(t.name));
  return (
    <div data-testid="facilitator-picker">
      <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Facilitators</span>
      <div className="flex flex-wrap gap-2 mb-2">
        {names.length === 0 && <span className="text-xs text-mahogany/50">No facilitators selected yet.</span>}
        {names.map((n) => (
          <span key={n} data-testid={`facilitator-chip-${n}`} className="inline-flex items-center gap-1.5 text-sm bg-rose-soft/60 text-mahogany rounded-full pl-3 pr-2 py-1">
            {n}
            <button type="button" onClick={() => remove(n)} className="text-mahogany/70 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <select value="" data-testid="facilitator-select"
          onChange={(e) => { addTeacher(e.target.value); e.target.value = ''; }}
          className="field">
          <option value="">+ Add existing facilitator…</option>
          {available.map((t) => <option key={t.id} value={t.id}>{t.name}{t.role ? ` — ${t.role}` : ''}</option>)}
        </select>
        <div className="flex gap-2">
          <input value={custom} onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
            placeholder="Add other facilitator name" data-testid="facilitator-custom-input" className="field flex-1" />
          <button type="button" onClick={addCustom} data-testid="facilitator-add-btn" className="pill-btn py-1.5"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default ProgramForm;
